import { useEffect } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { isBareShortcut } from '@/lib/hotkeys'
import { INBOX_KEY, useInboxFocus } from './inboxHotkey'

/**
 * `i` — go to the inbox, cursor already in the capture field.
 *
 * Renders nothing; it exists so the listener has somewhere to live that is
 * mounted for the whole session, next to the two shortcuts it sits beside.
 *
 * It navigates *and* asks for focus, because those are two different failures.
 * Navigating alone leaves the cursor nowhere when you were already on the
 * inbox, since nothing remounts and `autoFocus` has already had its one
 * chance. Asking for focus alone does nothing from another screen, because the
 * field is not on it yet.
 */
export function InboxShortcut() {
  const navigate = useNavigate()
  const request = useInboxFocus((state) => state.request)
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!isBareShortcut(event, INBOX_KEY)) return

      // Otherwise the `i` that brought you here is typed into the field it
      // just focused — the same trap `/` and `c` each had.
      event.preventDefault()

      if (pathname !== '/inbox') void navigate({ to: '/inbox' })
      request()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate, pathname, request])

  return null
}
